"use client";

import { CallStatus } from "@prisma/client";
import IconDownload from "@/app/(components)/generics/Icons/IconDownload";
import IconTrash from "@/app/(components)/generics/Icons/IconTrash";
import {
  useBrand,
  useCanManageCompany,
} from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import { ModalStore } from "@/app/stores/ModalStore";
import { UserStore } from "@/app/stores/UserStore";
import { TKeyCallAnalysis as TKey } from "@/app/translations/keys/TKeyCallAnalysis";
import { useT } from "@/app/translations/useT";
import { CallFile } from "@/app/types/call/CallFile";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import RunAnalysis, { RunAnalysisMode } from "../../RunAnalysis/RunAnalysis";
import styles from "../CallDetail.module.css";

type Props = DefaultProps & {
  callFile: CallFile;
  onRemove: () => void;
  onStatusChange: (status: CallStatus) => void;
};

export default function CallDetailFooter({ params, callFile, onRemove, onStatusChange }: Props) {
  const T = useT();
  const isAnalyzed = callFile.status === CallStatus.ANALYZED;

  const isTenant = useBrand() !== null;
  const canManageCompany = useCanManageCompany();
  const currentUserId = UserStore((state) => state.user?.id);
  // Managers manage every call; a plain member may still delete the calls they uploaded.
  const canManage = !isTenant || canManageCompany;
  const canDelete = canManage || callFile.savedCall?.uploaderId === currentUserId;

  const handleExport = () => {
    if (!callFile.savedCall?.id) return;
    ModalStore.getState().openExportCall(params, callFile.savedCall.id);
  };

  return (
    <div className={styles.footerActions}>
      {canDelete && (
        <button
          className={styles.footerBtnDanger}
          onClick={onRemove}
          title={T(TKey.CALL_DETAIL_DELETE) as string}
        >
          <IconTrash />
          <span>{T(TKey.CALL_DETAIL_DELETE) as string}</span>
        </button>
      )}

      <div className={styles.footerActionsRight}>
        {isAnalyzed && (
          <button
            className={styles.footerBtnGhost}
            onClick={handleExport}
            title={T(TKey.CALL_EXPORT_CALL_SUBMIT) as string}
          >
            <IconDownload />
            <span>{T(TKey.CALL_EXPORT_CALL_SUBMIT) as string}</span>
          </button>
        )}

        <RunAnalysis
          params={params}
          callId={callFile.id}
          onStart={onStatusChange}
          mode={RunAnalysisMode.FULL}
          callFile={callFile}
        />
      </div>
    </div>
  );
}
